import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useUserRole } from "./useUserRole";
import { useCompanySwitcher } from "./useCompanySwitcher";

/**
 * Determines if the active company has an active subscription.
 * - System owners are always allowed.
 * - "active" / "trialing" status counts as active, even if cancel_at_period_end=true,
 *   as long as current_period_end is in the future (or null).
 * - Subscribes to realtime changes on the subscriptions row so gate updates live
 *   without requiring a re-login.
 */
export function useSubscriptionGate() {
  const { user, loading: authLoading } = useAuth();
  const { isSystemOwner, isLoading: rolesLoading } = useUserRole();
  const { activeCompanyId, loadingCompanies } = useCompanySwitcher();
  const [hasActiveSub, setHasActiveSub] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [realtimeStatus, setRealtimeStatus] = useState<"connecting" | "connected" | "error" | "disabled">("connecting");

  // Track the latest companyId for the realtime fallback poll without
  // re-subscribing the channel.
  const companyIdRef = useRef<string | null>(null);
  companyIdRef.current = activeCompanyId ?? null;

  const evaluate = useCallback(
    (data: { status?: string | null; current_period_end?: string | null } | null) => {
      const validStatus = data?.status === "active" || data?.status === "trialing";
      const periodOk = !data?.current_period_end || new Date(data.current_period_end) > new Date();
      return !!(validStatus && periodOk);
    },
    [],
  );

  const fetchAndSet = useCallback(
    async (companyId: string) => {
      const { data } = await supabase
        .from("subscriptions")
        .select("status, current_period_end")
        .eq("company_id", companyId)
        .maybeSingle();
      setHasActiveSub(evaluate(data));
    },
    [evaluate],
  );

  // Effect 1: initial fetch + state when user / company / role context changes.
  // This runs on route-independent inputs so navigation does NOT trigger it.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (authLoading || rolesLoading || loadingCompanies) return;
      if (!user) {
        if (!cancelled) { setHasActiveSub(null); setLoading(false); setRealtimeStatus("disabled"); }
        return;
      }
      if (isSystemOwner) {
        if (!cancelled) { setHasActiveSub(true); setLoading(false); setRealtimeStatus("disabled"); }
        return;
      }
      if (!activeCompanyId) {
        if (!cancelled) { setHasActiveSub(false); setLoading(false); setRealtimeStatus("disabled"); }
        return;
      }
      setLoading(true);
      await fetchAndSet(activeCompanyId);
      if (!cancelled) setLoading(false);
    };
    run();
    return () => { cancelled = true; };
  }, [user, authLoading, rolesLoading, loadingCompanies, isSystemOwner, activeCompanyId, fetchAndSet]);

  // Effect 2: realtime channel — only re-subscribes when the *identity* of the
  // listener actually changes (user id, company id, or system-owner status).
  // Route changes don't touch these deps, so the channel stays open.
  useEffect(() => {
    if (!user || isSystemOwner || !activeCompanyId) return;

    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    // Backoff: 2s → 4s → 8s → … capped at 60s. Reset on SUBSCRIBED.
    let reconnectAttempt = 0;
    const MAX_BACKOFF_MS = 60_000;
    // Polling: starts aggressive (5s) when realtime is down so the gate
    // catches up fast, then ramps down to 60s to avoid load.
    const POLL_FAST_MS = 5_000;
    const POLL_SLOW_MS = 60_000;
    let pollIntervalMs = POLL_FAST_MS;

    const schedulePoll = () => {
      if (cancelled) return;
      pollTimer = setTimeout(async () => {
        if (cancelled) return;
        const id = companyIdRef.current;
        if (id) await fetchAndSet(id);
        // Ramp down: each tick doubles the interval up to the slow ceiling
        // so we stay responsive right after a disconnect but back off over time.
        pollIntervalMs = Math.min(pollIntervalMs * 2, POLL_SLOW_MS);
        schedulePoll();
      }, pollIntervalMs) as unknown as ReturnType<typeof setInterval>;
    };
    const startPolling = () => {
      if (pollTimer) { clearTimeout(pollTimer as unknown as ReturnType<typeof setTimeout>); pollTimer = null; }
      pollIntervalMs = POLL_FAST_MS;
      schedulePoll();
    };

    const stopPolling = () => {
      if (pollTimer) { clearTimeout(pollTimer as unknown as ReturnType<typeof setTimeout>); pollTimer = null; }
      pollIntervalMs = POLL_FAST_MS;
    };

    setRealtimeStatus("connecting");

    // Debounce the actual channel creation so that rapid company switches
    // (e.g. user clicking through the switcher) collapse into a single
    // subscription instead of opening + tearing down several in a row.
    const debounceMs = 250;
    const connect = () => {
      if (cancelled) return;
      // Tear down any previous channel before reconnecting.
      if (channel) {
        try { channel.unsubscribe(); } catch { /* noop */ }
        try { supabase.removeChannel(channel); } catch { /* noop */ }
        channel = null;
      }
      // Unique tag so a late-arriving subscribe() callback from a previous
      // (already-removed) channel can't flip status on the current one.
      const channelTag = `${activeCompanyId}:${Date.now()}:${Math.random()}`;

      channel = supabase
        .channel(`sub-gate-${channelTag}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "subscriptions",
            filter: `company_id=eq.${activeCompanyId}`,
          },
          (payload) => {
            const row = (payload.new ?? payload.old) as any;
            if (!cancelled) setHasActiveSub(evaluate(row));
          },
        )
        .subscribe((status) => {
          if (cancelled) return;
          // Log so we can see what's actually happening in the console.
          // Helps diagnose persistent "error" states in production.
          console.debug("[sub-gate] channel status:", status, channelTag);
          if (status === "SUBSCRIBED") {
            setRealtimeStatus("connected");
            reconnectAttempt = 0;
            stopPolling();
            // Re-sync immediately in case we missed events while reconnecting.
            const id = companyIdRef.current;
            if (id) fetchAndSet(id);
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
            // CLOSED is intentionally NOT treated as an error — Supabase emits
            // it during normal teardown (unmount, company switch, StrictMode
            // double-invoke), and flagging it would surface a false-positive
            // "Realtime unavailable" toast on every navigation.
            setRealtimeStatus("error");
            // Start aggressive polling immediately so the user isn't stuck.
            if (!pollTimer) startPolling();
            // Schedule a reconnect with exponential backoff + jitter.
            if (!reconnectTimer) {
              const base = Math.min(2_000 * 2 ** reconnectAttempt, MAX_BACKOFF_MS);
              const jitter = Math.random() * 0.3 * base;
              const delay = base + jitter;
              reconnectAttempt += 1;
              reconnectTimer = setTimeout(() => {
                reconnectTimer = null;
                if (!cancelled) {
                  setRealtimeStatus("connecting");
                  connect();
                }
              }, delay);
            }
          }
        });
    };

    const debounceTimer = setTimeout(connect, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pollTimer) clearTimeout(pollTimer as unknown as ReturnType<typeof setTimeout>);
      // Always tear down — wrap in try/catch in case the channel was already
      // removed (e.g. transport closed) so we never leave dangling listeners.
      if (channel) {
        try { channel.unsubscribe(); } catch { /* noop */ }
        try { supabase.removeChannel(channel); } catch { /* noop */ }
      }
    };
  }, [user?.id, isSystemOwner, activeCompanyId, evaluate, fetchAndSet]);

  return { hasActiveSub, loading, isSystemOwner, realtimeStatus };
}

/**
 * Routes that remain accessible even without an active subscription.
 * Returns true if the given pathname should bypass the subscription gate.
 */
export function isGateAllowedPath(pathname: string, canManageBilling: boolean = true): boolean {
  // Routes employees (non-billing-managers) may reach when subscription is paused.
  // Kept intentionally minimal: only the paused-info page, auth flows and legal pages.
  const employeeAllowed = [
    "/auth",
    "/legal",
    "/invitation",
    "/reset-password",
    "/unsubscribe",
    "/subscription-paused",
  ];
  // Owners/admins additionally need access to billing management surfaces.
  const ownerExtra = [
    "/onboarding",
    "/checkout-success",
    "/checkout-cancelled",
    "/settings",
    "/data-request",
  ];
  const prefixes = canManageBilling ? [...employeeAllowed, ...ownerExtra] : employeeAllowed;
  if (prefixes.some((p) => pathname === p || pathname.startsWith(p + "/"))) {
    return true;
  }
  // /companies hosts the subscription tab — owners/admins only.
  if (canManageBilling && (pathname === "/companies" || pathname.startsWith("/companies/"))) {
    return true;
  }
  return false;
}
